import React, { ReactNode } from "react";
import { AiOutlineCheck } from "react-icons/ai";
import { FaUserFriends } from "react-icons/fa";
import { HiChevronRight } from "react-icons/hi";
import { MdContentCopy } from "react-icons/md";
import { ColorRing } from "react-loader-spinner";

import { Button, Link } from "shared/components";
import {
  CouponCodeDiscountType,
  FriendCouponCode,
  FriendCouponCodeRewardDiscountType,
} from "shared/models";
import { BFC } from "shared/types";

import { useAuthContext } from "~/features/auth";
import { useCopy } from "~/utils/useCopy";

import { useCurrentUser } from "../../hooks";

export const FriendCoupon = () => {
  const { isLoading } = useAuthContext();
  const { currentUser, isLoading: isLoadingCurrentUser } = useCurrentUser();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center w-screen h-screen">
        <ColorRing />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 pb-4">
      <div className="bg-white">
        <h1 className="flex items-center gap-2 font-bold px-4 py-6 text-lg">
          <FaUserFriends size={24} className="text-black-500" />
          お友達紹介
        </h1>
      </div>
      <div className="bg-white">
        <div className="flex flex-col divide-y">
          <MenuItem
            label="あなたの紹介コード"
            note={
              !isLoadingCurrentUser && currentUser.friendCouponCode ? (
                <FriendCouponCodeDetail
                  friendCouponCode={currentUser.friendCouponCode}
                />
              ) : (
                <div>
                  紹介コードは電話番号認証が完了すると発行されます<br />
                  電話番号認証は、{currentUser.lastName}様の初回注文の際に購入画面に進むことで表示されます。<br />
                  お好きな商品をカートに追加し、画面右上の🛒ボタンを押し、「レジに進む」ボタンを押してください。<br />
                  実際に初回購入を完了しなくても、電話番号認証を完了することができます。
                </div>
              )
            }
          />
        </div>
      </div>
    </div>
  );
};

type MenuItemProps = {
  label: ReactNode;
  note?: ReactNode;
  href?: string;
};

const MenuItem: BFC<MenuItemProps> = ({ label, note, href }) => {
  return href ? (
    <Link
      href={href}
      noDecoration
      className="flex items-center justify-between p-4 gap-4"
    >
      <div className="flex flex-col gap-2">
        {label}
        {note && <div className="text-black-400">{note}</div>}
      </div>
      <HiChevronRight size={24} className="text-black-300" />
    </Link>
  ) : (
    <div className="flex flex-col gap-2 p-4">
      {label}
      {note && <div className="text-black-400">{note}</div>}
    </div>
  );
};

type FriendCouponCodeDetailProps = {
  friendCouponCode: FriendCouponCode;
};

const FriendCouponCodeDetail: BFC<FriendCouponCodeDetailProps> = ({
  friendCouponCode,
}) => {
  const { copy, copiedRecently } = useCopy();

  return (
    <div className="flex flex-col gap-6 pt-4">
      <div className="flex gap-4 justify-between w-full">
        <div className="text-xl font-bold text-gray-600 mt-1">
          {friendCouponCode.code}
        </div>

        <div className="flex flex-col gap-2 items-end">
          <Button onClick={() => copy(friendCouponCode.code)}>
            {copiedRecently(friendCouponCode.code) ? (
              <AiOutlineCheck className="text-green-500" />
            ) : (
              <MdContentCopy />
            )}
          </Button>
          {copiedRecently(friendCouponCode.code) ? (
            <span className="text-sm text-green-500">コピーしました</span>
          ) : (
            <span className="text-sm text-gray-400">
              コードをコピーできます
            </span>
          )}
        </div>
      </div>
      <hr />
      <div className="items-center flex max-w-md">
        <img src="/assets/images/mypage/friend-coupon.png" />
      </div>
      <ul className="list-disc ml-4 flex flex-col gap-3 text-gray-600">
        <li>
          上記のコードを、まだSocTocを使ったことがないお友達にLINEやメールで送ったり、SNSでシェアしたりしましょう！
        </li>
        <li>
          お友達がこのクーポンコードを初回購入時に使用すると
          <span className="font-bold text-lg">
            {friendCouponCode.discountType === CouponCodeDiscountType.Amount
              ? `${friendCouponCode.amountOff}円`
              : `${friendCouponCode.percentOff}%`}
          </span>
          {(friendCouponCode.discountType === CouponCodeDiscountType.Amount && friendCouponCode.amountOff > 500) ? "（通常500円、期間限定で増額中）" : ""}
          分の割引を受けることができます。
        </li>
        <li>
          さらに、紹介者であるあなたにも
          <span className="font-bold text-lg">
            {friendCouponCode.reward.discountType ===
            FriendCouponCodeRewardDiscountType.Amount
              ? `${friendCouponCode.reward.amountOff}円`
              : `${friendCouponCode.reward.percentOff}%`}
          </span>
          {friendCouponCode.reward.discountType === FriendCouponCodeRewardDiscountType.Amount && friendCouponCode.reward.amountOff > 500 ? "（通常500円、期間限定で増額中）" : ""}
          分の紹介特典クーポンが発行されます。
        </li>
        <li>
          紹介特典クーポンはお友達が初回注文を行い商品の受け取りが完了したタイミングで発行され、発行されると紹介したあなたにメールで通知されます。
        </li>
        <li>
          紹介特典クーポンは、紹介が成立したお友達の人数分
          何枚でも発行され、有効期限は発行されてから1年間です。
        </li>
      </ul>
    </div>
  );
};
